import Trace from "./snake.lib.trace.js";
import Storage from "./snake.lib.storage.js";
import config from "../config.js";
import Identity from "./snake.lib.identity.js";
import * as Sentry from "@sentry/browser";

/**
 * Analytics Service Layer
 */
export default class AnalyticsService {
    /**
     *
     * @returns
     */
    static #loadData = () => {
        const storagePaymentMethod = Storage.get("payment_method");
        const initData = JSON.parse(Storage.get("init")) || window.snakeCheckout.initData;

        Trace.debug("#loadData");
        Trace.print(initData);

        return {
            ...initData,
            checkout: {
                cart: {
                    id: Storage.get("cart_" + window.snakeCheckout.currentCountry),
                },
                order: {
                    id: Storage.get("IOD"),
                },
                paymentMethod: config.payments[storagePaymentMethod] ?? storagePaymentMethod ?? "",
            },
        };
    };

    /**
     *
     */
    static checkoutInitLoad = () => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            Trace.debug("[Analytics]: checkoutInitLoad");

            const storageParams = this.#loadData();
            // Trace.debug("Local storage query debug: ");
            Trace.print(storageParams);

            const _dataLayer = {
                event: "begin_checkout",
                page: this.getDataLayerPageNode({
                    id: "checkout",
                    type: "checkout",
                }),
                user: this.getDataLayerUserNode(storageParams),
                cart: this.getDataLayerCartDetail(storageParams),
                ecommerce: {
                    currencyCode: config.countries[window.snakeCheckout.currentCountry].currencyCode,
                    checkout: {
                        actionField: {
                            step: "1",
                        },
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }
    };

    /**
     *
     * @param {*} vars
     */
    static checkoutSignOutEvent = (label) => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: checkoutSignOutEvent");

            if (["icon", "exit", "continue_checkout"].indexOf(label) > -1) {
                const storageParams = this.#loadData();
                const _dataLayer = {
                    event: "imAnalyticsEvent",
                    event_data: {
                        category: "checkout",
                        action: "logout",
                        label,
                    },
                    products: this.getDataLayerProductDetail(storageParams),
                };

                Trace.print(_dataLayer);

                window.dataLayer.push(_dataLayer);
            } else {
                // Trace.warn("[Analytics]: checkoutSignOutEvent label errata, evento non inviato");
            }
        }
    };

    /**
     *
     * @param {*} vars
     */
    static checkoutPremiumBanner = (callback) => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: checkoutPremiumBanner");

            const storageParams = this.#loadData();

            const _dataLayer = {
                event: "imAnalyticsEvent",
                event_data: {
                    category: "checkout",
                    action: "click_cta",
                    label: "premium_subscription",
                },
                product: this.getDataLayerProductDetail(storageParams),
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }

        if (callback) callback();
    };

    /**
     *
     */
    static checkoutShippingLoad = () => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            Trace.debug("[Analytics]: checkoutShippingLoad");

            const storageParams = this.#loadData();

            Trace.print(storageParams);

            const _dataLayer = {
                event: "add_shipping_info",
                page: this.getDataLayerPageNode({
                    id: "checkout",
                    type: "checkout",
                }),
                user: this.getDataLayerUserNode(storageParams),
                cart: this.getDataLayerCartDetail(storageParams),
                ecommerce: {
                    currencyCode: config.countries[window.snakeCheckout.currentCountry].currencyCode,
                    checkout: {
                        actionField: {
                            step: "3",
                        },
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }
    };

    /**
     *
     */
    static checkoutPaymentLoad = () => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: checkoutPaymentLoad");

            const storageParams = this.#loadData();

            const _dataLayer = {
                event: "add_payment_info",
                page: this.getDataLayerPageNode({
                    id: "checkout",
                    type: "checkout",
                }),
                user: this.getDataLayerUserNode(storageParams),
                order: this.getDataLayerOrderDetail(storageParams),
                cart: this.getDataLayerCartDetail(storageParams),
                ecommerce: {
                    currencyCode: config.countries[window.snakeCheckout.currentCountry].currencyCode,
                    checkout: {
                        actionField: {
                            step: "4",
                        },
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }
    };

    /**
     *
     */
    static checkoutPaymentExecute = () => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: checkoutPaymentExecute");

            const storageParams = this.#loadData();

            const _dataLayer = {
                event: "try_pay",
                page: this.getDataLayerPageNode({
                    id: "checkout",
                    type: "checkout",
                }),
                user: this.getDataLayerUserNode(storageParams),
                order: this.getDataLayerOrderDetail(storageParams),
                cart: this.getDataLayerCartDetail(storageParams),
                ecommerce: {
                    currencyCode: config.countries[window.snakeCheckout.currentCountry].currencyCode,
                    checkout: {
                        actionField: {
                            step: "5",
                            action: "checkout",
                        },
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);

            const _dataLayerSecondary = {
                event: "checkoutOption",
                ecommerce: {
                    currencyCode: config.countries[window.snakeCheckout.currentCountry].currencyCode,
                    checkout_option: {
                        actionField: {
                            step: 4,
                            option: storageParams.checkout.paymentMethod,
                            action: "checkout_option",
                        },
                    },
                },
            };

            Trace.print(_dataLayerSecondary);

            window.dataLayer.push(_dataLayerSecondary);
        }
    };

    /**
     *
     * @param {*} vars
     */
    static checkoutPurchaseDone = async (revenue, callback) => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: checkoutPurchaseDone");

            window.snakeCheckout.revenue = revenue || 0;

            const storageParams = this.#loadData();
            let user = this.getDataLayerUserNode(storageParams);

            let cookiepro = false;
            const cookie = document.cookie.split("; ").find((cookieName) => cookieName.startsWith("OptanonConsent"));
            if (cookie) {
                const consents = new URLSearchParams(decodeURIComponent(cookie)).get("groups");
                if (consents?.includes("C0004:1") || consents?.includes("C0005:1")) cookiepro = true;
            }

            if (cookiepro) {
                await Identity.getAccountInfo()
                    .then((response) => {
                        // Trace.debug("[Analytics]: checkoutPurchaseDone getAccountInfo response");
                        // Trace.log(response);

                        /**
                         * Get user default shipping addresses related to current country
                         */
                        const shippingAddress = (Array.isArray(response.data.shippingAddress) ? response.data.shippingAddress : []).filter((address) => address.normCountryCode === config.countries[window.snakeCheckout.currentCountry].countryCodeGigya && address.defaultAddress);

                        user = {
                            first_name: response.profile.firstName || "",
                            last_name: response.profile.lastName || "",
                            email: response.profile.email || "",
                            phone_number: shippingAddress[0].phoneNumber || "",
                            home_address: [
                                {
                                    street: ((shippingAddress[0].addressLine1 || "") + " " + (shippingAddress[0].addressLine2 || "")).trim(),
                                    city: shippingAddress[0].city || "",
                                    postal_code: shippingAddress[0].postalCode || "",
                                    country: window.snakeCheckout.currentCountry,
                                },
                            ],
                            ...user,
                            id: response.UID || "",
                            login_status: "logged",
                        };
                    })
                    .catch((e) => {
                        Trace.debug("checkoutPurchaseDone getAccountInfo error");
                        Trace.debug(e);

                        this.errorEvent("4_typ", "technical", "gproxy | get user account info error", e);
                    });
            }

            const _dataLayer = {
                event: "purchase",
                page: this.getDataLayerPageNode({
                    id: "purchase",
                    type: "typ",
                }),
                user,
                cart: this.getDataLayerCartDetail(storageParams),
                ecommerce: {
                    currencyCode: config.countries[window.snakeCheckout.currentCountry].currencyCode,
                    purchase: {
                        actionField: {
                            id: storageParams.checkout.order.id,
                            revenue: window.snakeCheckout.revenue,
                            tax: "",
                            shipping: 0,
                            coupon: Storage.get("promotions") ?? "",
                            affiliation: "",
                            action: "purchase",
                            date: this.getCurrentDate(),
                        },
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }

        if (callback) callback();
    };

    /**
     *
     */
    static checkoutPurchaseFailed = () => {
        // // Trace.debug("[Analytics]: checkoutPurchaseFailed");
        // const _dataLayer = {
        //   event: "try_pay_ko",
        // };
        // // Trace.print(_dataLayer);
        // window.dataLayer.push(_dataLayer);
    };

    /**
     *
     * @param bool isOpen
     */
    static summaryDrawerIsOpen = (isOpen) => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: summaryDrawerIsOpen");

            const storageParams = this.#loadData();

            const _dataLayer = {
                event: "imAnalyticsEvent",
                event_data: {
                    category: "E-commerce",
                    action: "click_summary",
                    label: isOpen ? "open" : "close",
                },
                ecommerce: {
                    detail: {
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }
    };

    /**
     *
     * @param bool isPremium
     */
    static summaryArrows = (isPremium) => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            // Trace.debug("[Analytics]: summaryArrows");

            const storageParams = this.#loadData();

            const _dataLayer = {
                event: "imAnalyticsEvent",
                event_data: {
                    category: "checkout",
                    action: isPremium ? "premium" : "gifts",
                    label: "swiper-slider",
                },
                products: this.getDataLayerProductDetail(storageParams),
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }
    };

    /**
     *
     * @param string detail
     */
    static fullKitUpSelling = (action) => {
        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            const storageParams = this.#loadData();

            const _dataLayer = {
                event: "imAnalyticsEvent",
                event_data: {
                    category: "checkout",
                    action,
                    label: "",
                },
                ecommerce: {
                    detail: {
                        products: this.getDataLayerProductDetail(storageParams),
                    },
                },
            };

            Trace.print(_dataLayer);

            window.dataLayer.push(_dataLayer);
        }
    };

    /**
     *
     * @param string step
     * @param string type
     * @param string detail
     * @param object error
     */
    static errorEvent = (step, type, detail, error = null) => {
        Trace.debug("[Analytics]: errorEvent");

        step = step || "";
        type = type || "";
        detail = Identity.isAgent() || Identity.isAgentCustomer() ? "agent, " + (detail || "") : detail || "";

        const paymentMethod = Storage.get("payment_method");

        if (import.meta.env.CO_ANALYTICS_ERROR_ENABLED === "true") {
            const _dataLayer = {
                event: "error_checkout",
                error_checkout_step: step,
                error_checkout_tipology: type,
                error_checkout_tipology_detail: detail,
                error_payment_type: paymentMethod,
            };

            window.dataLayer.push(_dataLayer);

            Trace.debug(_dataLayer);
        }

        Trace.debug(error);

        if (import.meta.env.CO_SENTRY_ENABLED === "true" && error) {
            const cause = error?.cause ?? {
                name: error?.name ?? "",
                message: error?.message ?? "",
                stack: error?.stack ?? "",
                data: JSON.stringify(
                    {
                        iod: window?.snakeCheckout?.iod ?? "missing",
                        cart_id: window?.snakeCheckout?.initData?.cart_id ?? "missing",
                        gigyaId: window?.snakeCheckout?.accountInfo?.gigyaId ?? "missing",
                        ...(error?.data ?? {}),
                        params: window?.location?.search,
                        payment_type: paymentMethod ?? "missing",
                    },
                    null,
                    2
                ),
            };

            error = Error((step + " " + type + " " + detail).trim(), { cause });

            Sentry.setContext("Context", error.cause);
            Sentry.captureException(error);
        }
    };

    /**
     *
     * @param object params
     */
    static getDataLayerPageNode = (params) => {
        // Trace.debug(window.snakeCheckout.currentCountry);

        return {
            id: params?.id ?? "checkout",
            type: params?.type ?? "checkout",
            brand: import.meta.env.CO_BRAND,
            country: window.snakeCheckout.currentCountry,
            division: "",
            language: config.countries[window.snakeCheckout.currentCountry].lang || window.snakeCheckout.currentCountry,
            platform: "responsive_web",
        };
    };

    /**
     *
     */
    static getDataLayerUserNode = () => {
        // const uid = Storage.get("uid");
        const uid = window.snakeCheckout.accountInfo?.gigyaId || "";

        return {
            id: uid !== "" && uid !== null ? uid : "",
            login_status: uid !== "" && uid !== null ? "logged" : "not_logged",
            subscriber: "",
            type: "",
            login_method: "",
            loyalty_points: "",
            loyalty_type: "",
        };
    };

    /**
     *
     * @param object storageParams
     */
    static getDataLayerProductDetail = (storageParams) => {
        Trace.debug("getDataLayerProductDetail");
        Trace.print(storageParams);

        const stripTags = (text) => {
            const element = new DOMParser().parseFromString(text, "text/html");
            return element.body.textContent || "";
        };

        const products = [];
        if (storageParams && Array.isArray(storageParams.products) && storageParams.products.length > 0 && Array.isArray(storageParams.analytics)) {
            storageParams.products.forEach((product, index) => {
                products.push({
                    id: product.code || "",
                    name: storageParams.analytics[index]?.name || stripTags(product.name) || "",
                    brand: import.meta.env.CO_BRAND,
                    category: storageParams.analytics[index]?.category || "",
                    price: window.snakeCheckout.revenue || product.price || 0,
                    quantity: product.quantity || 1,
                    variant: product.sku || "",
                    dimension8: "Model", // @todo: -- build up / collectibles
                    dimension10: storageParams.analytics[index]?.launchStatus || "",
                    dimension22: storageParams.analytics[index]?.launch_date || "",
                    dimension25: product.type || window.snakeCheckout.type,
                    dimension26: "",
                    dimension27: product.edition || "",
                    dimension28: product.starting_issue || "",
                    dimension29: storageParams.checkout.paymentMethod || "",
                    dimension38: product.plan || "",
                    dimension42: "no",
                });
            });
        }

        return products;
    };

    /**
     *
     * @param object storageParams
     */
    static getDataLayerOrderDetail = (storageParams) => {
        return {
            id: storageParams.checkout.order.id,
            date: "",
        };
    };

    /**
     *
     * @param object storageParams
     */
    static getDataLayerCartDetail = (storageParams) => {
        return {
            id: storageParams.checkout.cart.id,
        };
    };

    static getCurrentDate = () => {
        const date = new Date();
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        return String(day) + String(month) + String(year);
    };
}
